import React, { useState, useEffect, useContext } from 'react';
import api from '../../../services/api';
import UserEditModal from '../../../components/modals/UserEditModal';
import { TextField } from '@mui/material';
import DeleteConfirmationModal from './modals/DeleteConfirmationModal';
import { filterVisibleUsers } from '../../../utils/roleHierarchy';
import { AuthContext } from '../../../context/AuthContext';

export default function SuperAdminUsers() {
  const { user: currentUser } = useContext(AuthContext);

  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Estados para os filtros
  const [nameFilter, setNameFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users');
      const sortedUsers = res.data.sort((a, b) => a.fullName.localeCompare(b.fullName));
      setUsers(sortedUsers);
    } catch (err) {
      console.error('Erro ao buscar usuários:', err);
      setError('Falha ao carregar dados.');
    }
  };

  const fetchCompanies = async () => {
    try {
      const res = await api.get('/companies');
      setCompanies(res.data);
    } catch (err) {
      console.error('Erro ao buscar empresas:', err);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Busca usuários e empresas em paralelo para otimizar o carregamento
        await Promise.all([fetchUsers(), fetchCompanies()]);
      } catch (err) {
        setError('Falha ao carregar dados.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleOpenModal = (user = null) => {
    setSelectedUser(user);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
    document.body.style.overflow = '';
  };

  const handleSaveUser = async (dataToSave, isCreating) => {
    try {
      let res;
      if (isCreating) {
        res = await api.post('/users/create', dataToSave);
        await fetchUsers(); // Recarrega para garantir consistência
        // Retorna o usuário criado para o modal abrir o CourseEnrollmentModal
        return res.data;
      }
      res = await api.put(`/users/${selectedUser.userName}`, dataToSave);
      setUsers(prev => prev.map(u => (u.id === res.data.id ? res.data : u)));

      handleCloseModal();
      return res.data;
    } catch (error) {
      console.error('Erro ao salvar usuário:', error);
      throw error; // Re-throw para que o modal possa capturar
    }
  };

  const handleDeleteClick = user => {
    setItemToDelete(user);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setItemToDelete(null);
    setIsDeleteModalOpen(false);
  };

  const handleConfirmDelete = deletedUser => {
    // A API call é feita dentro do modal. Esta função apenas atualiza o estado.
    setUsers(prev => prev.filter(u => u.id !== deletedUser.id));
    handleCloseDeleteModal();
  };

  // Funções auxiliares para exibir o papel do usuário
  const getRoleLabel = role => {
    const roles = {
      SUPER_ADMIN: 'Super Admin',
      ADMIN_EAD: 'Admin (EAD)',
      ADMIN_CLIENT: 'Admin (Cliente)',
      INSTRUCTOR: 'Instrutor',
      STUDENT: 'Aluno',
    };
    return roles[role] || 'Desconhecido';
  };

  const getRoleBadgeClass = role => {
    const classes = {
      SUPER_ADMIN: 'role-badge super-admin-role',
      ADMIN_EAD: 'role-badge admin-role',
      ADMIN_CLIENT: 'role-badge admin-role client',
      INSTRUCTOR: 'role-badge instructor',
      STUDENT: 'role-badge student',
    };
    return classes[role] || 'role-badge';
  };

  // Lógica de filtragem no cliente
  const filteredUsers = users.filter(user => {
    const nameMatch = !nameFilter || user.fullName.toLowerCase().includes(nameFilter.toLowerCase());
    const emailMatch = !emailFilter || user.email.toLowerCase().includes(emailFilter.toLowerCase());
    return nameMatch && emailMatch;
  });

  // Filtrar usuários visíveis com base na hierarquia
  const visibleUsers = filterVisibleUsers(filteredUsers, currentUser?.role);

  if (loading)
    return (
      <div className="loading-state">
        <div className="loading-spinner"></div> Carregando usuários...
      </div>
    );
  if (error) return <div className="error-message">{error}</div>;

  return (
    <>
      <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
        <div className="card-body">
          <h3 className="filter-title">Filtros de Usuários</h3>
          <div
            className="filter-form"
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}
          >
            <div className="form-group">
              <TextField
                id="name-filter"
                label="Buscar por Nome"
                placeholder="Nome do usuário..."
                value={nameFilter}
                onChange={e => setNameFilter(e.target.value)}
                fullWidth
              />
            </div>
            <div className="form-group">
              <TextField
                id="email-filter"
                label="Buscar por Email"
                placeholder="Email do usuário..."
                value={emailFilter}
                onChange={e => setEmailFilter(e.target.value)}
                fullWidth
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 'var(--space-4)',
            }}
          >
            <h2>Todos os Usuários ({visibleUsers.length})</h2>
            <button className="btn btn-primary" onClick={() => handleOpenModal()}>
              + Criar Novo Usuário
            </button>
          </div>
          <div className="table-wrapper">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Email</th>
                  <th>Tipo</th>
                  <th>Empresa</th>
                  <th style={{ width: '1%' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {visibleUsers.map((user, idx) => (
                  <tr
                    key={user.id || user.userName || idx}
                    onClick={() => handleOpenModal(user)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <div className="user-info">
                        <div className="user-avatar">
                          {user.fullName ? user.fullName.charAt(0).toUpperCase() : 'U'}
                        </div>
                        {user.fullName || 'Usuário'}
                      </div>
                    </td>
                    <td>{user.email || '-'}</td>
                    <td>
                      <span className={getRoleBadgeClass(user.role)}>
                        {getRoleLabel(user.role)}
                      </span>
                    </td>
                    <td>
                      {user.company?.name || <span className="no-company">Não informado</span>}
                    </td>
                    <td>
                      <div className="table-actions" onClick={e => e.stopPropagation()}>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleOpenModal(user)}
                        >
                          Editar
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteClick(user)}
                        >
                          Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <UserEditModal
          user={selectedUser}
          onClose={handleCloseModal}
          onSuccess={handleSaveUser}
          companies={companies}
        />
      )}

      {isDeleteModalOpen && (
        <DeleteConfirmationModal
          open={isDeleteModalOpen}
          onClose={handleCloseDeleteModal}
          onConfirmSuccess={handleConfirmDelete}
          itemType="user"
          item={itemToDelete}
        />
      )}
    </>
  );
}
